import { Badge, notification, Spin } from "antd";
import { CheckOutlined, SendOutlined } from "@ant-design/icons";
import { InternalPageLayout } from "components";
import { usePermissions } from "hooks";
import { UIEvent, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { collection, onSnapshot } from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import { auth, db } from "config/firebase";

const CHAT_PAGE_SIZE = 10;
const CHAT_SCROLL_THRESHOLD = 48;

interface WhatsAppConversation {
    conversationId: string;
    contactWaId: string;
    displayName: string | null;
    phoneNumber: string;
    lastMessageAt: any;
    sessionExpiresAt: any;
    sessionIsActive: boolean;
    totalMessages: number;
}

interface ConversationDetail extends WhatsAppConversation {
    returnedMessages: number;
    skip: number;
    take: number;
    userMessages: any[];
    botMessages: any[];
}

const getAvatarInitials = (displayName?: string | null) => {
    const value = displayName?.trim();
    if (!value) return "WA";
    const parts = value.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatDateTime = (value: any) => dayjs(value).format("DD/MMM/YYYY hh:mm A");

const formatPhoneNumber = (value?: string | null) => {
    if (!value) return "Sin numero";

    const digits = value.replace(/\D/g, "");
    const localNumber = digits.length === 13 && digits.startsWith("521")
        ? digits.slice(3)
        : digits.length > 10 && digits.startsWith("52")
            ? digits.slice(2)
            : digits;

    if (localNumber.length !== 10) return value;

    return `${localNumber.slice(0, 2)} ${localNumber.slice(2, 6)} ${localNumber.slice(6)}`;
};

const normalizeMessage = (message: any) => ({
    body: message.Body ?? message.body ?? "",
    eventTimestamp: normalizeTimestamp(message.EventTimestamp ?? message.eventTimestamp),
    fromMe: message.FromMe ?? message.fromMe,
    messageId: message.MessageId ?? message.messageId,
    messageType: message.MessageType ?? message.messageType,
    mimeType: message.MimeType ?? message.mimeType,
    sender: message.Sender ?? message.sender,
    status: message.Status ?? message.status,
    waMessageId: message.WaMessageId ?? message.waMessageId
});

const normalizeTimestamp = (value: any) => {
    if (value && typeof value.toDate === "function") return value.toDate();
    if (value?.seconds !== undefined) return new Date(value.seconds * 1000);
    return value;
};

const normalizeConversation = (documentId: string, conversation: any): ConversationDetail => {
    const userMessages = (conversation.UserMessages ?? conversation.userMessages ?? []).map(normalizeMessage);
    const botMessages = (conversation.BotMessages ?? conversation.botMessages ?? []).map(normalizeMessage);
    const totalMessages = conversation.TotalMessages ?? conversation.totalMessages ?? Math.max(userMessages.length, botMessages.length);
    const messages = [...userMessages, ...botMessages].filter((message) => message.eventTimestamp);
    const lastMessageAt = conversation.LastMessageAt ?? conversation.lastMessageAt ?? messages
        .sort((first, second) => dayjs(second.eventTimestamp).valueOf() - dayjs(first.eventTimestamp).valueOf())[0]?.eventTimestamp ?? "";
    const phoneNumber = conversation.PhoneNumber ?? conversation.phoneNumber ?? "";
    const maskedPhoneNumber = formatPhoneNumber(phoneNumber);

    return {
        conversationId: conversation.ConversationId ?? conversation.conversationId ?? documentId,
        contactWaId: conversation.ContactWaId ?? conversation.contactWaId ?? "",
        displayName: conversation.DisplayName ?? conversation.displayName ?? maskedPhoneNumber,
        phoneNumber,
        lastMessageAt: normalizeTimestamp(lastMessageAt),
        sessionExpiresAt: normalizeTimestamp(conversation.SessionExpiresAt ?? conversation.sessionExpiresAt),
        sessionIsActive: conversation.SessionIsActive ?? conversation.sessionIsActive ?? false,
        totalMessages,
        returnedMessages: conversation.ReturnedMessages ?? conversation.returnedMessages ?? 0,
        skip: conversation.Skip ?? conversation.skip ?? 0,
        take: conversation.Take ?? conversation.take ?? CHAT_PAGE_SIZE,
        userMessages,
        botMessages
    };
};

const WhatsAppBot = () => {
    const { can } = usePermissions();
    const canWrite = can("whatsapp", "write");
    const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
    const [selectedChatTitle, setSelectedChatTitle] = useState<string>("");
    const [selectedChatTitlePhone, setSelectedChatTitlePhone] = useState<string>("");
    const [chatLoading, setChatLoading] = useState(false);
    const [chatLoadingMore, setChatLoadingMore] = useState(false);
    const [isMessagesLoading, setIsMessagesLoading] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const chatLoaderTimeoutRef = useRef<number | null>(null);
    const [messageText, setMessageText] = useState("");
    const [sendingMessage, setSendingMessage] = useState(false);
    const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(null);
    const loadingMoreRef = useRef(false);
    const chatScrollRef = useRef<HTMLDivElement | null>(null);
    const conversationDocumentsRef = useRef<ConversationDetail[]>([]);
    const selectedPhoneRef = useRef<string | null>(null);
    const selectedConversationRef = useRef<ConversationDetail | null>(null);
    const selectedConversationDetailRef = useRef<ConversationDetail | null>(null);
    const [panelHeight, setPanelHeight] = useState<number>(window.innerWidth > 1800 ? 750 : 520);

    useEffect(() => {
        const updatePanelHeight = () => {
            setPanelHeight(window.innerWidth > 1800 ? 750 : 520);
        };

        updatePanelHeight();
        window.addEventListener('resize', updatePanelHeight);
        return () => window.removeEventListener('resize', updatePanelHeight);
    }, []);

    useEffect(() => {
        let isMounted = true;
        let unsubscribe: (() => void) | undefined;

        const loadConversations = async () => {
            setIsLoading(true);
            try {
                if (!auth.currentUser) {
                    await signInAnonymously(auth);
                }
                if (!isMounted) return;

                unsubscribe = onSnapshot(
                    collection(db, "conversations"),
                    (snapshot) => {
                        const conversationsData = snapshot.docs.map((document) =>
                            normalizeConversation(document.id, document.data())
                        );
                        conversationDocumentsRef.current = conversationsData;
                        const sorted = [...conversationsData].sort((a, b) =>
                            dayjs(b.lastMessageAt).valueOf() - dayjs(a.lastMessageAt).valueOf()
                        );
                        setConversations(sorted);
                        const isDetailViewActive = Boolean(selectedPhoneRef.current) && Boolean(selectedConversationDetailRef.current);
                        const updatedConversation = conversationsData.find((conversation) =>
                            String(conversation.phoneNumber) === String(selectedPhoneRef.current)
                        );
                        const currentConversation = selectedConversationRef.current;
                        if (!isDetailViewActive && updatedConversation && currentConversation) {
                            const loadedMessages = currentConversation.skip;
                            const refreshedConversation = {
                                ...updatedConversation,
                                skip: Math.min(loadedMessages, updatedConversation.totalMessages),
                                returnedMessages: Math.min(loadedMessages, updatedConversation.totalMessages),
                                userMessages: updatedConversation.userMessages.slice(0, loadedMessages),
                                botMessages: updatedConversation.botMessages.slice(0, loadedMessages)
                            };
                            selectedConversationRef.current = refreshedConversation;
                            setSelectedConversation(refreshedConversation);
                            setHasMoreMessages(loadedMessages < (updatedConversation.totalMessages || 0));
                        }
                        setIsLoading(false);
                    },
                    (error) => {
                        console.error("Error escuchando conversaciones:", error);
                        notification.error({
                            message: "Error",
                            description: error.message || "No se pudieron cargar las conversaciones"
                        });
                        setIsLoading(false);
                    }
                );

            } catch (error) {
                console.error("Error cargando conversaciones:", error);
                notification.error({
                    message: "Error",
                    description: "No se pudo cargar el historial de conversaciones"
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadConversations();

        return () => {
            isMounted = false;
            unsubscribe?.();
        };
    }, []);

    const conversationItems = useMemo(() => {
        return conversations.map((conversation) => {
            const isActive = conversation.sessionIsActive;
            const formattedPhone = formatPhoneNumber(conversation.phoneNumber);
            const title = conversation.displayName?.trim() || formattedPhone || "Sin nombre";
            return (
                <div
                    key={conversation.conversationId}
                    onClick={() => {
                        setSelectedPhone(conversation.phoneNumber);
                        selectedPhoneRef.current = conversation.phoneNumber;
                        setSelectedChatTitle(title);
                        setSelectedChatTitlePhone(formattedPhone);
                        setSelectedConversation(null);
                        setIsMessagesLoading(true);
                    }}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: 16,
                        borderBottom: "1px solid #D8CDBF",
                        background: selectedPhone === conversation.phoneNumber ? "#E9E2D8" : (isActive ? "#EEE4D3" : "#F1E6D8"),
                        color: isActive ? "#4A4238" : "#7E6954",
                        opacity: isActive ? 1 : 0.85,
                        cursor: "pointer"
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                        <div
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                background: isActive ? "#7E6954" : "#DCDCCF",
                                color: isActive ? "#FFF8E6" : "#6B5A49",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: 14,
                                flexShrink: 0
                            }}
                        >
                            {getAvatarInitials(title)}
                        </div>
                        <div style={{ minWidth: "88%" }}>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {title}
                            </div>
             
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, width: "100%", minWidth: 0 }}>
                                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    Total Mensajes: {conversation.totalMessages}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div style={{ textAlign: "right", minWidth: 100, fontSize: 12, whiteSpace: "nowrap", height: 50, lineHeight: "20px", flexDirection: "column", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 4, textTransform: "capitalize" }}>

                                                        <Badge
                                    style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                                    status={isActive ? "success" : "error"}
                                    text={isActive ? "Activo" : "Inactivo"}
                                />
                                                        {formatDateTime(conversation.lastMessageAt)}
                    </div>
                </div>
            );
        });
    }, [conversations, selectedPhone]);

    useEffect(() => {
        if (!selectedPhone) {
            setSelectedConversation(null);
            selectedConversationRef.current = null;
            selectedConversationDetailRef.current = null;
            setChatLoading(false);
            setIsMessagesLoading(false);
            return;
        }

        if (chatLoaderTimeoutRef.current) {
            window.clearTimeout(chatLoaderTimeoutRef.current);
        }

        setIsMessagesLoading(true);
        setSelectedConversation(null);

        const scrollToBottom = () => {
            requestAnimationFrame(() => {
                if (!chatScrollRef.current) return;
                chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
            });
        };

        let unsubscribe: (() => void) | undefined;

        const loadByPhone = () => {
            setChatLoading(true);
            setChatLoadingMore(false);
            setIsMessagesLoading(true);
            setHasMoreMessages(false);
            loadingMoreRef.current = false;
            setSelectedConversation(null);
            selectedConversationRef.current = null;
            selectedConversationDetailRef.current = null;

            unsubscribe = onSnapshot(
                collection(db, "conversations-by-phone"),
                (snapshot) => {
                    const matchingConversation = snapshot.docs.find((document) => {
                        const data = document.data();
                        const phoneValue = data.PhoneNumber ?? data.phoneNumber ?? document.id;
                        return String(phoneValue) === String(selectedPhone) || String(document.id) === String(selectedPhone);
                    });

                    if (!matchingConversation) {
                        setSelectedConversation(null);
                        selectedConversationRef.current = null;
                        selectedConversationDetailRef.current = null;
                        setChatLoading(false);
                        setIsMessagesLoading(false);
                        return;
                    }

                    const data = normalizeConversation(matchingConversation.id, matchingConversation.data());
                    selectedConversationDetailRef.current = data;

                    const returnedMessages = data.returnedMessages || Math.max(
                        data.userMessages?.length || 0,
                        data.botMessages?.length || 0
                    );
                    const nextConversation = {
                        ...data,
                        skip: Math.min(CHAT_PAGE_SIZE, returnedMessages),
                        returnedMessages,
                        userMessages: (data.userMessages || []).slice(0, CHAT_PAGE_SIZE),
                        botMessages: (data.botMessages || []).slice(0, CHAT_PAGE_SIZE)
                    };

                    selectedConversationRef.current = nextConversation;
                    setSelectedConversation(nextConversation);
                    setHasMoreMessages(CHAT_PAGE_SIZE < (data.totalMessages || returnedMessages));
                    setChatLoading(false);

                    chatLoaderTimeoutRef.current = window.setTimeout(() => {
                        setIsMessagesLoading(false);
                    }, 2000);

                    requestAnimationFrame(() => {
                        if (chatScrollRef.current) {
                            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
                        }
                    });
                },
                (error) => {
                    console.error("Error escuchando conversacion por telefono:", error);
                    notification.error({
                        message: "Error",
                        description: "No se pudo cargar la conversacion seleccionada"
                    });
                    setChatLoading(false);
                    setIsMessagesLoading(false);
                }
            );
        };

        loadByPhone();
        scrollToBottom();

        return () => unsubscribe?.();
    }, [selectedPhone]);

    const loadMoreMessages = async () => {
        if (!selectedPhone || !selectedConversation || !hasMoreMessages || loadingMoreRef.current) return;

        loadingMoreRef.current = true;
        setChatLoadingMore(true);
        const skip = selectedConversation.skip;

        try {
            const data = selectedConversationDetailRef.current;
            if (!data) throw new Error("No se encontro la conversacion seleccionada");

            const totalMessages = data.totalMessages || Math.max(
                data.userMessages?.length || 0,
                data.botMessages?.length || 0
            );
            const returnedMessages = Math.min(CHAT_PAGE_SIZE, totalMessages - skip);

            const currentConversation = selectedConversationRef.current;
            const nextConversation = currentConversation ? {
                ...currentConversation,
                skip: skip + returnedMessages,
                returnedMessages: Math.min((currentConversation.returnedMessages || 0) + returnedMessages, totalMessages),
                userMessages: [...(currentConversation.userMessages || []), ...(data.userMessages || []).slice(skip, skip + CHAT_PAGE_SIZE)],
                botMessages: [...(currentConversation.botMessages || []), ...(data.botMessages || []).slice(skip, skip + CHAT_PAGE_SIZE)]
            } : null;
            selectedConversationRef.current = nextConversation;
            setSelectedConversation(nextConversation);
            setHasMoreMessages(skip + returnedMessages < totalMessages);
        } catch (error) {
            console.error(error);
            notification.error({ message: 'Error', description: 'No se pudieron cargar mas mensajes' });
        } finally {
            loadingMoreRef.current = false;
            setChatLoadingMore(false);
        }
    };

    const interleaveMessages = (user: any[], bot: any[]) => {
        const out: any[] = [];
        const max = Math.max(user?.length || 0, bot?.length || 0);
        for (let i = 0; i < max; i++) {
            if (user && user[i]) out.push(user[i]);
            if (bot && bot[i]) out.push(bot[i]);
        }
        return out;
    };

    useEffect(() => {
        if (!selectedConversation || isMessagesLoading) return;
        requestAnimationFrame(() => {
            if (!chatScrollRef.current) return;
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        });
    }, [selectedConversation, isMessagesLoading]);

    const sendMessage = async () => {
        const trimmedMessage = messageText.trim();
        if (!selectedPhone || !trimmedMessage || sendingMessage) return;

        setSendingMessage(true);

        try {
            const response = await fetch("https://message.goodlook.com.mx/api/message/send", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    phone: String(selectedPhone),
                    message: trimmedMessage,
                    template: null,
                    language_code: null,
                    supportActive: true
                })
            });

            const responseText = await response.text();
            let payload: any = null;
            try {
                payload = responseText ? JSON.parse(responseText) : null;
            } catch {
                payload = null;
            }

            if (!response.ok) {
                throw new Error(payload?.message || payload?.error || `Error HTTP ${response.status}`);
            }

            setMessageText("");
            notification.success({
                message: "Mensaje enviado",
                description: "El mensaje se envio correctamente."
            });
        } catch (error) {
            console.error("Error enviando mensaje:", error);
            notification.error({
                message: "Error",
                description: error instanceof Error ? error.message : "No se pudo enviar el mensaje"
            });
        } finally {
            setSendingMessage(false);
        }
    };

    const isBotMessage = (message: any) => {
        const sender = String(message?.sender ?? "").toLowerCase();
        return Boolean(message?.fromMe) || sender === "business" || sender === "bot";
    };

    const renderChatView = ({ conversation, chatTitle, selectedChatTitlePhone }: { conversation: ConversationDetail; chatTitle: string; selectedChatTitlePhone: string }) => (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "start", gap: 12, paddingBottom: 12, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                <div
                    style={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        background: conversation.sessionIsActive ? "#7E6954" : "#DCDCCF",
                        color: conversation.sessionIsActive ? "#FFF8E6" : "#6B5A49",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 14,
                        lineHeight: 1,
                        flexShrink: 0
                    }}
                >
                    {getAvatarInitials(chatTitle)}
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{chatTitle}</div>
                    <div style={{ fontWeight: 400 }}>{selectedChatTitlePhone}</div>
                    <div style={{ fontSize: 12, color: "#5C5244" }}>{conversation.sessionIsActive ? "Activo" : "Inactivo"}</div>
                </div>
            </div>

            <div
                ref={chatScrollRef}
                onScroll={(event: UIEvent<HTMLDivElement>) => {
                    const element = event.currentTarget;
                    const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
                    if (distanceToBottom <= CHAT_SCROLL_THRESHOLD) loadMoreMessages();
                }}
                style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}
            >
                {chatLoading ? (
                    <div style={{ textAlign: "center" }}><Spin spinning tip="Cargando chat..." /></div>
                ) : (
                    interleaveMessages(conversation.userMessages || [], conversation.botMessages || []).map((m: any, idx: number) => {
                        const isBot = isBotMessage(m);
                        const isUser = !isBot;
                        const bubbleBackground = isBot ? '#DCF8C6' : '#FFFFFF';
                        const borderColor = isBot ? '#CDE7B0' : '#E8E0D5';

                        return (
                            <div key={m.messageId || idx} style={{ display: "flex", justifyContent: isUser ? 'flex-start' : 'flex-end' }}>
                                <div style={{
                                    maxWidth: '70%',
                                    padding: '10px 12px',
                                    borderRadius: 12,
                                    background: bubbleBackground,
                                    border: `1px solid ${borderColor}`,
                                    color: '#111',
                                    boxShadow: '0 1px 0 rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                                    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-start' : 'flex-end', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11, color: '#666' }}>
                                        <span>{dayjs(m.eventTimestamp).format('hh:mm A')}</span>
                                        {isBot && m.status && <CheckOutlined style={{ color: m.status === 'read' ? '#1890ff' : '#999', fontSize: 12 }} />}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                {chatLoadingMore && <div style={{ textAlign: "center" }}><Spin size="small" tip="Cargando mas mensajes..." /></div>}
            </div>
            <div style={{ paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: 12 }}>
                {canWrite &&
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <textarea
                        value={messageText}
                        onChange={(event) => setMessageText(event.target.value)}
                        placeholder="Escribe un mensaje..."
                        style={{
                            flex: 1,
                            minHeight: 56,
                            resize: 'none',
                            borderRadius: 20,
                            border: '1px solid rgba(0,0,0,0.12)',
                            padding: '12px 16px',
                            background: '#FFFFFF',
                            color: '#2F2F2F',
                            outline: 'none',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
                            fontSize: 14,
                            lineHeight: 1.4
                        }}
                    />
                    <button
                        onClick={sendMessage}
                        disabled={!selectedPhone || !messageText.trim() || sendingMessage}
                        style={{
                            width: 48,
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            border: 'none',
                            background: !selectedPhone || !messageText.trim() || sendingMessage ? '#D8D8D8' : '#FF0072',
                            color: '#FFFFFF',
                            cursor: !selectedPhone || !messageText.trim() || sendingMessage ? 'not-allowed' : 'pointer',
                            boxShadow: !selectedPhone || !messageText.trim() || sendingMessage ? 'none' : '0 4px 10px rgba(255, 0, 114, 0.25)',
                            transition: 'all 0.2s ease',
                            opacity: sendingMessage ? 0.8 : 1
                        }}
                        title="Enviar mensaje"
                    >
                        <SendOutlined style={{ fontSize: 18 }} />
                    </button>
                </div>}
            </div>
        </div>
    );

    return (
        <InternalPageLayout title="Bitacora Bot">
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div style={{
                    height: panelHeight,
                    minWidth: 320,
                    background: "#FFFFFF",
                    borderRadius: 20,
                    boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
                    overflow: "hidden"
                }}>
                    <div style={{ padding: 22, background: "#FF0072", color: "#FFFFFF" }}>
                        <h3 style={{ margin: 0, fontSize: 18, color: " #FFFFFF" }}>Conversaciones</h3>
                        <p style={{ margin: "8px 0 0", color: "#FFFFFF", fontSize: 13 }}>Historial ordenado por ultima actividad</p>
                    </div>
                    <div style={{ height: panelHeight, overflowY: 'auto', background: "#F7F5EF" }}>
                        <Spin spinning={isLoading} tip="Cargando conversaciones...">
                            {conversationItems.length > 0 ? (
                                conversationItems
                            ) : (
                                <div style={{ padding: 40, textAlign: "center", color: "#7E6954" }}>
                                    {isLoading ? null : <span>No hay conversaciones disponibles.</span>}
                                </div>
                            )}
                        </Spin>
                    </div>
                </div>

                <div style={{
                    flex: 1,
                    minWidth: 320,
                    background: "#F1ECE1",
                    borderRadius: 20,
                    display: "flex",
                    flexDirection: "column",
                    padding: 24,
                    height: panelHeight,
                    color: "#6B5A49",
                    boxShadow: "0 8px 28px rgba(0,0,0,0.05)"
                }}>
                    {selectedPhone && (!selectedConversation || isMessagesLoading) ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <div style={{ textAlign: 'center' }}>
                                <Spin size="large" tip="Cargando mensajes..." />
                            </div>
                        </div>
                    ) : selectedConversation ? (
                        renderChatView({ conversation: selectedConversation, chatTitle: selectedChatTitle, selectedChatTitlePhone: selectedChatTitlePhone })
                    ) : (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                            <div style={{textAlign: 'center'}}>
                                <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Selecciona una conversacion</p>
                                <p style={{ marginTop: 12, color: "#5C5244" }}>Aqui aparecera el contenido cuando elijas un chat.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </InternalPageLayout>
    );
};

export default WhatsAppBot;
